import { useId, useState } from 'react'
import { Check, X } from 'lucide-react'
import {
  getRetellApiErrorMessage,
  useBindRetellAgent,
  type BindRetellAgentArgs,
  type RetellPhoneAgentKind,
} from '../../api/admin-retell-phones'

interface AddRetellAgentBindingModalProps {
  orgId: string
  phoneId: string
  phoneE164: string
  onClose: () => void
}

// Datalist suggestions sourced from ``configs/call_purposes.json``. Hardcoded
// here intentionally — the file is operator-managed and pulling it across the
// wire would couple the admin UI to a config endpoint that doesn't exist
// today. The select-via-datalist UX still lets the operator type free-form
// values for new purposes (the backend stores ``purpose`` as opaque text).
const PURPOSE_SUGGESTIONS = [
  'lead_qualification',
  'follow_up',
  'confirmation',
  'csat_followup',
]

/**
 * "Bind Retell agent to phone" modal.
 *
 * The ``agent_id`` field is a free-text input rather than a dropdown — Retell
 * has no parallel of the VAPI ``/api/admin/vapi-assistants`` list endpoint,
 * so operators paste the ``agent_xxx...`` id from their Retell dashboard.
 * Once a list endpoint lands this should be swapped for a ``<select>``.
 *
 * ``kind`` is a required enum (``outbound``/``inbound``/``both``) — matches
 * the backend's ``CreateRetellPhoneAgentBody``. ``purpose`` is optional and
 * uses a datalist for the common values (``lead_qualification`` etc.) while
 * still allowing arbitrary strings for org-specific purposes.
 */
export function AddRetellAgentBindingModal({
  orgId,
  phoneId,
  phoneE164,
  onClose,
}: AddRetellAgentBindingModalProps) {
  const [agentId, setAgentId] = useState('')
  const [kind, setKind] = useState<RetellPhoneAgentKind>('outbound')
  const [purpose, setPurpose] = useState('')
  const bind = useBindRetellAgent(orgId, phoneId)
  const purposeListId = useId()

  const errMessage = getRetellApiErrorMessage(bind.error)
  const submitDisabled = agentId.trim().length === 0 || bind.isPending

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const args: BindRetellAgentArgs = {
      agent_id: agentId.trim(),
      kind,
      purpose: purpose.trim() || null,
    }
    try {
      await bind.mutateAsync(args)
      onClose()
    } catch {
      // Surfaced inline via ``bind.error``.
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="font-medium">Bind Retell agent</div>
            <div className="font-mono text-xs text-text-secondary">
              {phoneE164}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border p-1 hover:bg-surface-elevated"
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <form onSubmit={submit} className="space-y-3 p-4">
          <label className="block space-y-1 text-sm">
            <span className="text-text-secondary">
              Retell agent ID <span className="text-error">*</span>
            </span>
            <input
              type="text"
              value={agentId}
              onChange={(ev) => setAgentId(ev.target.value)}
              placeholder="agent_xxxxxxxxxxxxxxxxxxxxxxxx"
              required
              autoFocus
              className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
            />
            <div className="text-xs text-text-secondary">
              Paste the agent id from your Retell dashboard.
            </div>
          </label>

          <label className="block space-y-1 text-sm">
            <span className="text-text-secondary">
              Kind <span className="text-error">*</span>
            </span>
            <select
              value={kind}
              onChange={(ev) =>
                setKind(ev.target.value as RetellPhoneAgentKind)
              }
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            >
              <option value="outbound">outbound</option>
              <option value="inbound">inbound</option>
              <option value="both">both</option>
            </select>
          </label>

          <label className="block space-y-1 text-sm">
            <span className="text-text-secondary">Purpose</span>
            <input
              type="text"
              value={purpose}
              onChange={(ev) => setPurpose(ev.target.value)}
              list={purposeListId}
              placeholder="lead_qualification"
              className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
            />
            <datalist id={purposeListId}>
              {PURPOSE_SUGGESTIONS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <div className="text-xs text-text-secondary">
              Optional. Leave blank to make this the default-for-kind binding.
            </div>
          </label>

          {bind.isError && errMessage && (
            <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
              {errMessage}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitDisabled}
              className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              {bind.isPending ? 'Binding…' : 'Bind agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
